import axios from "axios";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
  getSafeUserMessage,
} from "./errorMessages";

function getEnv(name) {
  if (typeof process !== "undefined" && process.env?.[name]) {
    return String(process.env[name]).trim();
  }

  return "";
}

function clampNumber(value, min, max, fallback) {
  const num = Number(value);

  if (!Number.isFinite(num)) return fallback;

  return Math.min(max, Math.max(min, num));
}

function resolveBaseURL() {
  const envURL = getEnv("REACT_APP_API_URL");
  const raw = envURL || "/api";

  return raw.replace(/\/+$/, "");
}

function isProd() {
  return getEnv("NODE_ENV") === "production";
}

function isSafeMethod(method = "") {
  return ["get", "head", "options"].includes(
    String(method || "")
      .trim()
      .toLowerCase(),
  );
}

function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function getRequestPath(config = {}) {
  return String(config.url || "")
    .trim()
    .toLowerCase();
}

function isAuthRoute(url = "") {
  const path = String(url || "")
    .trim()
    .toLowerCase();

  return (
    path.includes("/auth/login") ||
    path.includes("/auth/register") ||
    path.includes("/auth/verify-email") ||
    path.includes("/auth/resend-verification") ||
    path.includes("/auth/refresh-token") ||
    path.includes("/auth/logout") ||
    path.includes("/auth/check-session") ||
    path.includes("/auth/forgot-password") ||
    path.includes("/auth/reset-password")
  );
}

function isCsrfBootstrapRoute(url = "") {
  return String(url || "")
    .trim()
    .toLowerCase()
    .includes("/csrf-token");
}

function shouldSkipRefresh(config = {}) {
  return config.skipAuthRefresh === true;
}

function getHeader(headers, name) {
  if (!headers) return undefined;

  if (typeof headers.get === "function") {
    return headers.get(name);
  }

  const exact = headers[name];
  if (exact !== undefined) return exact;

  const lowerName = name.toLowerCase();

  const foundKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === lowerName,
  );

  return foundKey ? headers[foundKey] : undefined;
}

function setHeader(headers, name, value) {
  if (!headers) return;

  if (typeof headers.set === "function") {
    headers.set(name, value);
    return;
  }

  headers[name] = value;
}

function hasHeader(headers, name) {
  const value = getHeader(headers, name);
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function normalizeError(error) {
  if (!error) {
    return Promise.reject(new Error(USER_FACING_FALLBACK));
  }

  error.normalizedMessage = getApiErrorMessage(error);
  error.statusCode = Number(error?.response?.status || 0);
  error.errorCode = String(error?.response?.data?.code || "");
  error.retryAfterSeconds = Number(
    error?.response?.data?.retryAfterSeconds ||
      error?.response?.headers?.["retry-after"] ||
      0,
  );
  error.requestId = String(
    error?.response?.data?.requestId ||
      error?.response?.headers?.["x-request-id"] ||
      "",
  );
  error.safeDebug = {
    code: error.errorCode,
    route: getRequestPath(error?.config || {}),
    status: error.statusCode,
  };

  try {
    error.message = error.normalizedMessage;
  } catch {
    // Some error implementations expose message as read-only.
  }

  if (error.response?.data && typeof error.response.data === "object") {
    error.response.data.message = error.normalizedMessage;
    error.response.data.customerMessage = getSafeUserMessage(
      error.response.data.customerMessage,
      error.normalizedMessage,
    );
  }

  return Promise.reject(error);
}

const baseURL = resolveBaseURL();

const DEFAULT_TIMEOUT_MS = clampNumber(
  getEnv("REACT_APP_API_TIMEOUT_MS") || 15000,
  3000,
  30000,
  15000,
);

const defaultConfig = {
  baseURL,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    Accept: "application/json",
  },
};

const api = axios.create(defaultConfig);

// Transport has no interceptors.
// This prevents infinite loops during CSRF bootstrap and refresh requests.
const transport = axios.create(defaultConfig);

let refreshPromise = null;
let csrfPromise = null;
let csrfTokenCache = "";

async function fetchCsrfToken() {
  const { data } = await transport.get("/csrf-token", {
    withCredentials: true,
    skipAuthRefresh: true,
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-store",
    },
  });

  const token = String(data?.csrfToken || "").trim();

  if (!token) {
    throw new Error(
      "We could not verify this request. Refresh the page and try again.",
    );
  }

  csrfTokenCache = token;
  return token;
}

async function ensureCsrfToken({ force = false } = {}) {
  if (!force && csrfTokenCache) {
    return csrfTokenCache;
  }

  if (!csrfPromise) {
    csrfPromise = fetchCsrfToken().finally(() => {
      csrfPromise = null;
    });
  }

  return csrfPromise;
}

async function buildRefreshRequestConfig() {
  const csrfToken = await ensureCsrfToken();

  const headers = {
    Accept: "application/json",
  };

  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  return {
    withCredentials: true,
    skipAuthRefresh: true,
    skipCsrf: true,
    headers,
  };
}

api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};

  if (!hasHeader(config.headers, "Accept")) {
    setHeader(config.headers, "Accept", "application/json");
  }

  if (typeof config.withCredentials !== "boolean") {
    config.withCredentials = true;
  }

  if (!config.timeout) {
    config.timeout = DEFAULT_TIMEOUT_MS;
  }

  const hasContentType = hasHeader(config.headers, "Content-Type");

  if (config.data != null && !hasContentType && !isFormData(config.data)) {
    setHeader(config.headers, "Content-Type", "application/json");
  }

  const requestPath = getRequestPath(config);

  const shouldAttachCsrf =
    !isSafeMethod(config.method) &&
    config.skipCsrf !== true &&
    !isCsrfBootstrapRoute(requestPath);

  if (shouldAttachCsrf) {
    const csrfToken = await ensureCsrfToken();

    if (csrfToken) {
      setHeader(config.headers, "X-CSRF-Token", csrfToken);
    }
  }

  if (!isProd() && getEnv("REACT_APP_DEBUG_API") === "true") {
    console.log("API request", {
      method: config.method,
      url: config.url,
      withCredentials: config.withCredentials,
      hasCsrfHeader: Boolean(getHeader(config.headers, "X-CSRF-Token")),
    });
  }

  return config;
}, normalizeError);

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error?.config;

    if (!originalRequest) {
      return normalizeError(error);
    }

    const requestPath = getRequestPath(originalRequest);
    const status = Number(error?.response?.status || 0);
    const errorCode = String(error?.response?.data?.code || "").trim();

    if (status === 429) {
      return normalizeError(error);
    }

    const shouldRetryCsrf =
      status === 403 &&
      !originalRequest._csrfRetry &&
      !isSafeMethod(originalRequest.method) &&
      !isCsrfBootstrapRoute(requestPath) &&
      (errorCode === "CSRF_TOKEN_MISSING" ||
        errorCode === "CSRF_TOKEN_INVALID" ||
        errorCode === "EBADCSRFTOKEN");

    if (shouldRetryCsrf) {
      originalRequest._csrfRetry = true;
      csrfTokenCache = "";

      try {
        const csrfToken = await ensureCsrfToken({ force: true });

        originalRequest.headers = originalRequest.headers || {};

        if (csrfToken) {
          setHeader(originalRequest.headers, "X-CSRF-Token", csrfToken);
        }

        return api.request(originalRequest);
      } catch (csrfError) {
        return normalizeError(csrfError);
      }
    }

    const shouldTryRefresh =
      status === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh(originalRequest) &&
      !isAuthRoute(requestPath) &&
      !isCsrfBootstrapRoute(requestPath);

    if (!shouldTryRefresh) {
      return normalizeError(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshConfig = await buildRefreshRequestConfig();
            await transport.post("/auth/refresh-token", {}, refreshConfig);
          } catch (refreshError) {
            const refreshCode = String(
              refreshError?.response?.data?.code || "",
            ).trim();

            const isRefreshCsrfFailure =
              refreshError?.response?.status === 403 &&
              (refreshCode === "CSRF_TOKEN_MISSING" ||
                refreshCode === "CSRF_TOKEN_INVALID" ||
                refreshCode === "EBADCSRFTOKEN");

            if (!isRefreshCsrfFailure) {
              throw refreshError;
            }

            csrfTokenCache = "";

            const refreshConfig = await buildRefreshRequestConfig();
            await transport.post("/auth/refresh-token", {}, refreshConfig);
          }
        })().finally(() => {
          refreshPromise = null;
        });
      }

      await refreshPromise;

      return api.request(originalRequest);
    } catch (refreshError) {
      refreshError.originalAuthError = error;
      return normalizeError(refreshError);
    }
  },
);

export default api;
export { getApiErrorMessage, USER_FACING_FALLBACK };
